import { createTheme, alpha } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    tertiary: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
  }

  interface PaletteOptions {
    tertiary?: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
  }
}

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#007AFF', // Apple blue
      light: '#5AC8FA',
      dark: '#0051D4',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#722F37', // Wine red
      light: '#8B4A52',
      dark: '#5A1F24',
      contrastText: '#FFFFFF',
    },
    tertiary: {
      main: '#AF52DE', // Apple purple
      light: '#BF5AF2',
      dark: '#8E44AD',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F2F2F7', // iOS light gray background
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1C1C1E', // Apple dark gray
      secondary: '#8E8E93', // Apple medium gray
    },
    divider: alpha('#C6C6C8', 0.6),
    grey: {
      50: '#F2F2F7',
      100: '#E5E5EA',
      200: '#D1D1D6',
      300: '#C7C7CC',
      400: '#AEAEB2',
      500: '#8E8E93',
      600: '#636366',
      700: '#48484A',
      800: '#3A3A3C',
      900: '#1C1C1E',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.35,
      letterSpacing: '-0.015em',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.45,
      letterSpacing: '-0.005em',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.45,
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.45,
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.4,
      color: '#8E8E93',
    },
  },
  shape: {
    borderRadius: 12,
  },
  spacing: 8, // 8px base spacing unit
  shadows: [
    'none',
    '0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24)',
    '0px 3px 6px rgba(0, 0, 0, 0.16), 0px 3px 6px rgba(0, 0, 0, 0.23)',
    '0px 10px 20px rgba(0, 0, 0, 0.19), 0px 6px 6px rgba(0, 0, 0, 0.23)',
    '0px 14px 28px rgba(0, 0, 0, 0.25), 0px 10px 10px rgba(0, 0, 0, 0.22)',
    '0px 19px 38px rgba(0, 0, 0, 0.30), 0px 15px 12px rgba(0, 0, 0, 0.22)',
    '0px 24px 48px rgba(0, 0, 0, 0.35), 0px 19px 19px rgba(0, 0, 0, 0.22)',
    '0px 29px 58px rgba(0, 0, 0, 0.40), 0px 24px 24px rgba(0, 0, 0, 0.22)',
    '0px 29px 58px rgba(0, 0, 0, 0.40), 0px 24px 24px rgba(0, 0, 0, 0.22)',
    '0px 29px 58px rgba(0, 0, 0, 0.40), 0px 24px 24px rgba(0, 0, 0, 0.22)',
    '0px 29px 58px rgba(0, 0, 0, 0.40), 0px 24px 24px rgba(0, 0, 0, 0.22)',
    '0px 29px 58px rgba(0, 0, 0, 0.40), 0px 24px 24px rgba(0, 0, 0, 0.22)',
    '0px 29px 58px rgba(0, 0, 0, 0.40), 0px 24px 24px rgba(0, 0, 0, 0.22)',
    '0px 29px 58px rgba(0, 0, 0, 0.40), 0px 24px 24px rgba(0, 0, 0, 0.22)',
    '0px 29px 58px rgba(0, 0, 0, 0.40), 0px 24px 24px rgba(0, 0, 0, 0.22)',
    '0px 29px 58px rgba(0, 0, 0, 0.40), 0px 24px 24px rgba(0, 0, 0, 0.22)',
    '0px 29px 58px rgba(0, 0, 0, 0.40), 0px 24px 24px rgba(0, 0, 0, 0.22)',
    '0px 29px 58px rgba(0, 0, 0, 0.40), 0px 24px 24px rgba(0, 0, 0, 0.22)',
    '0px 29px 58px rgba(0, 0, 0, 0.40), 0px 24px 24px rgba(0, 0, 0, 0.22)',
    '0px 29px 58px rgba(0, 0, 0, 0.40), 0px 24px 24px rgba(0, 0, 0, 0.22)',
    '0px 29px 58px rgba(0, 0, 0, 0.40), 0px 24px 24px rgba(0, 0, 0, 0.22)',
    '0px 29px 58px rgba(0, 0, 0, 0.40), 0px 24px 24px rgba(0, 0, 0, 0.22)',
    '0px 29px 58px rgba(0, 0, 0, 0.40), 0px 24px 24px rgba(0, 0, 0, 0.22)',
    '0px 29px 58px rgba(0, 0, 0, 0.40), 0px 24px 24px rgba(0, 0, 0, 0.22)',
    '0px 29px 58px rgba(0, 0, 0, 0.40), 0px 24px 24px rgba(0, 0, 0, 0.22)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          backgroundColor: '#F2F2F7',
          margin: 0,
          padding: 0,
        },
        '*': {
          boxSizing: 'border-box',
        },
        '*, *::before, *::after': {
          boxSizing: 'inherit',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24)',
        },
        elevation2: {
          boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16), 0px 3px 6px rgba(0, 0, 0, 0.23)',
        },
        elevation3: {
          boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.19), 0px 6px 6px rgba(0, 0, 0, 0.23)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          borderRadius: 12,
          border: `1px solid ${alpha('#C6C6C8', 0.3)}`,
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16), 0px 3px 6px rgba(0, 0, 0, 0.23)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          padding: '8px 16px',
          transition: 'all 0.2s ease-in-out',
        },
        contained: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24)',
          '&:hover': {
            boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16), 0px 3px 6px rgba(0, 0, 0, 0.23)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        outlined: {
          borderColor: alpha('#C6C6C8', 0.5),
          '&:hover': {
            backgroundColor: alpha('#F2F2F7', 0.8),
            borderColor: '#8E8E93',
          },
        },
      },
    },
    /**
     * INPUT-AFFORDANCE.1 — every outlined input, not only the ones inside a
     * TextField.
     *
     * This treatment used to be nested under MuiTextField, as
     * `root > & .MuiOutlinedInput-root`. That selector only matches an input
     * INSIDE a .MuiTextField-root — so a <Select> in a <FormControl>, which
     * renders the same .MuiOutlinedInput-root without that wrapper, got none of
     * it and fell back to MUI's default grey hairline.
     *
     * There are 120 bare Selects in this app and ZERO TextField-select ones, so
     * in practice every dropdown in the product was the undressed kind. Against
     * white cards and soft shadows a default hairline does not read as
     * something you can click, which is how a Destination picker on the import
     * dialog — the field that decides where a whole file of stock lands — came
     * to look like a caption.
     *
     * Moved to MuiOutlinedInput so it applies wherever the class appears.
     * TextFields keep exactly the styling they had; they were already matching
     * this rule through the nested selector.
     */
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#FFFFFF',
          '& fieldset': {
            borderColor: alpha('#C6C6C8', 0.5),
          },
          '&:hover fieldset': {
            borderColor: '#8E8E93',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#007AFF',
            borderWidth: 2,
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${alpha('#C6C6C8', 0.3)}`,
          overflow: 'hidden',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#F2F2F7',
          // UI-CONSISTENCY.1 — the ONE table-header style. Pages must not
          // re-style header cells (the old per-page colStyle/thStyle consts are
          // retired). whiteSpace 'normal' is deliberate: with fixed table
          // layouts, headers WRAP to a second line instead of clipping
          // mid-character ("UNIT COS1"). Numeric columns right-align via the
          // per-column align prop, not here.
          '& .MuiTableCell-head': {
            fontWeight: 600,
            fontSize: '0.6875rem', // 11px
            lineHeight: 1.3,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: '#8E8E93', // palette.text.secondary
            whiteSpace: 'normal',
            verticalAlign: 'bottom',
            borderBottom: `1px solid ${alpha('#C6C6C8', 0.5)}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: alpha('#F2F2F7', 0.5),
          },
          '&:last-child .MuiTableCell-root': {
            borderBottom: 'none',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${alpha('#C6C6C8', 0.3)}`,
          padding: '12px 16px',
          fontSize: '0.875rem',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'saturate(180%) blur(20px)',
          borderBottom: `1px solid ${alpha('#C6C6C8', 0.3)}`,
          color: '#1C1C1E',
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          borderRight: `1px solid ${alpha('#C6C6C8', 0.3)}`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '0 8px',
          '&:hover': {
            backgroundColor: alpha('#F2F2F7', 0.8),
          },
          '&.Mui-selected': {
            backgroundColor: alpha('#007AFF', 0.1),
            '&:hover': {
              backgroundColor: alpha('#007AFF', 0.15),
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontSize: '0.75rem',
          fontWeight: 500,
        },
        outlined: {
          borderColor: alpha('#C6C6C8', 0.5),
        },
      },
    },
    // Warning Alert — soft cream/light-orange treatment.
    //
    // ONLY overrides severity="warning" with the default standard variant.
    // success / error / info Alerts are intentionally NOT touched and keep
    // MUI's defaults. Outlined and filled warning variants are also untouched
    // so callers can still opt into a louder treatment when needed.
    //
    // Recipe lifted from the existing inline sx pattern used by the wine
    // database forms (RegionForm, ProducerForm) so behaviour stays consistent.
    MuiAlert: {
      styleOverrides: {
        standardWarning: ({ theme }) => ({
          // Background and border slightly more saturated than the first pass
          // so the alert reads as "noticed" without being aggressive.
          backgroundColor: alpha(theme.palette.warning.main, 0.11),
          border: `1px solid ${alpha(theme.palette.warning.main, 0.38)}`,
          // Text leans warm in light mode (warm dark brown on cream reads as a
          // single tonal family with the background) but stays at text.primary
          // in dark mode so the body copy doesn't lose readability against the
          // dark surface — warm-brown text on dark surfaces is hard to read.
          color: theme.palette.mode === 'light'
            ? '#5a3d00'
            : theme.palette.text.primary,
          '& .MuiAlert-icon': {
            color: theme.palette.warning.main,
          },
          '& .MuiAlert-action .MuiIconButton-root': {
            color: theme.palette.text.secondary,
          },
        }),
      },
    },
  },
});
